/**
 * Be- und Abzüge: System-Kategorien (Label/Typ fix; pro Instanz Lohnart, Sichtbarkeit, Sortierung)
 * plus eigene Kategorien je Instanz (payroll_adjustment_lohnarten.is_custom).
 */

export type PayrollAdjustmentType = 'bezug' | 'abzug';

export type PayrollAdjustmentCategoryDef = {
  key: string;
  label: string;
  type: PayrollAdjustmentType;
  defaultLohnart: string;
};

export const PAYROLL_ADJUSTMENT_CATEGORIES: PayrollAdjustmentCategoryDef[] = [
  { key: 'abschlag', label: 'Abschlag/Vorschuss', type: 'abzug', defaultLohnart: '203' },
  { key: 'darlehen', label: 'Darlehensauszahlung', type: 'bezug', defaultLohnart: '' },
  { key: 'darlehen_rueckzahlung', label: 'Darlehensrückzahlung', type: 'abzug', defaultLohnart: '' },
  { key: 'erstattung_kosten', label: 'Erstattung verauslagte Kosten', type: 'bezug', defaultLohnart: '41' },
  { key: 'erstattung_kleidung', label: 'Erstattung Arbeitskleidung', type: 'bezug', defaultLohnart: '1012' },
  { key: 'praemie', label: 'Prämie', type: 'bezug', defaultLohnart: '' },
  { key: 'rueckwerkzeug', label: 'Rückzahlung Werkzeug', type: 'abzug', defaultLohnart: '1003' },
  { key: 'vertragsstrafe', label: 'Vertragsstrafe', type: 'abzug', defaultLohnart: '1004' },
  { key: 'arbeitskleidung_abzug', label: 'Abzug Arbeitskleidung', type: 'abzug', defaultLohnart: '1005' },
  { key: 'autoaufbereitung', label: 'Abzug Autoaufbereitung', type: 'abzug', defaultLohnart: '1006' },
  { key: 'fehlen', label: 'Abzug wegen unentschuldigten Fehlens', type: 'abzug', defaultLohnart: '1007' },
  { key: 'tankkosten', label: 'Abzug Tankkosten', type: 'abzug', defaultLohnart: '1008' },
  { key: 'autoreparatur', label: 'Abzug Autoreparatur', type: 'abzug', defaultLohnart: '1009' },
  { key: 'strafzettel', label: 'Abzug Strafzettel', type: 'abzug', defaultLohnart: '1010' },
  { key: 'uebernachtung', label: 'Übernachtungskosten Abzug', type: 'abzug', defaultLohnart: '1011' },
];

export const PAYROLL_ADJUSTMENT_CATEGORY_KEYS = new Set(
  PAYROLL_ADJUSTMENT_CATEGORIES.map((c) => c.key),
);

/** In separater Agenda-Datei exportieren (nicht in Lohnbuchungen mit VMA/ÜN/Zulage). */
export const PAYROLL_ADJUSTMENT_LOAN_CATEGORY_KEYS = new Set(['darlehen', 'darlehen_rueckzahlung']);

export function isPayrollAdjustmentLoanCategory(categoryKey: string): boolean {
  return PAYROLL_ADJUSTMENT_LOAN_CATEGORY_KEYS.has(categoryKey);
}

export type PayrollAdjustmentCategory = PayrollAdjustmentCategoryDef & {
  lohnart: string;
  /** Eigene Kategorie der Instanz (Name/Typ editierbar, löschbar solange unbenutzt). */
  isCustom?: boolean;
  /** Ausgeblendet: nicht mehr für neue Buchungen wählbar, bestehende laufen weiter. */
  hidden?: boolean;
  sortOrder?: number;
};

/** DB-Zeile aus payroll_adjustment_lohnarten (Overrides + eigene Kategorien). */
export type PayrollAdjustmentCategoryDbRow = {
  category_key: string;
  lohnart_code?: string | null;
  label?: string | null;
  adjustment_type?: string | null;
  is_custom?: boolean | null;
  is_hidden?: boolean | null;
  sort_order?: number | null;
};

export const PAYROLL_ADJUSTMENT_CUSTOM_KEY_RE = /^custom_[a-z0-9_]{1,48}$/;

export function isPayrollAdjustmentCustomKey(key: string): boolean {
  return PAYROLL_ADJUSTMENT_CUSTOM_KEY_RE.test(key);
}

export type PayrollAdjustmentEntryRow = {
  id: string;
  employee_id: string;
  employee_name: string;
  category_key: string;
  year_month: string;
  booking_date: string;
  amount: number;
  note: string;
  recurring: false;
};

export type PayrollAdjustmentRuleRow = {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_end_date?: string | null;
  category_key: string;
  amount: number;
  note: string;
  start_year_month: string;
  end_mode: 'exit' | 'date';
  end_year_month: string | null;
  day_of_month: number;
  active: boolean;
};

export type PayrollAdjustmentMonthRow = {
  id: string;
  entry_id: string | null;
  rule_id: string | null;
  employee_id: string;
  employee_name: string;
  category_key: string;
  booking_date: string;
  amount: number;
  note: string;
  recurring: boolean;
  type: PayrollAdjustmentType;
};

export function isPayrollAdjustmentCategoryKey(key: string): boolean {
  return PAYROLL_ADJUSTMENT_CATEGORY_KEYS.has(key);
}

export function getPayrollAdjustmentCategory(key: string): PayrollAdjustmentCategoryDef | undefined {
  return PAYROLL_ADJUSTMENT_CATEGORIES.find((c) => c.key === key);
}

/**
 * Kategorien einer Instanz: System-Kategorien (mit Lohnart/Sichtbarkeit/Sortierung aus der DB)
 * plus eigene Kategorien. Sortiert nach sort_order, dann System-Reihenfolge, eigene zuletzt.
 */
export function buildInstanceCategories(
  rows: PayrollAdjustmentCategoryDbRow[],
): PayrollAdjustmentCategory[] {
  const byKey = new Map(rows.map((r) => [String(r.category_key), r]));
  const out: Array<PayrollAdjustmentCategory & { _tie: number }> = [];

  PAYROLL_ADJUSTMENT_CATEGORIES.forEach((cat, idx) => {
    const row = byKey.get(cat.key);
    out.push({
      ...cat,
      lohnart: row ? String(row.lohnart_code ?? '').trim() : cat.defaultLohnart,
      isCustom: false,
      hidden: Boolean(row?.is_hidden),
      sortOrder: Number(row?.sort_order) || 0,
      _tie: idx,
    });
  });

  let customIdx = PAYROLL_ADJUSTMENT_CATEGORIES.length;
  for (const row of rows) {
    const key = String(row.category_key);
    if (!row.is_custom || isPayrollAdjustmentCategoryKey(key)) continue;
    const label = String(row.label ?? '').trim();
    if (!label) continue;
    out.push({
      key,
      label,
      type: row.adjustment_type === 'bezug' ? 'bezug' : 'abzug',
      defaultLohnart: '',
      lohnart: String(row.lohnart_code ?? '').trim(),
      isCustom: true,
      hidden: Boolean(row.is_hidden),
      sortOrder: Number(row.sort_order) || 0,
      _tie: customIdx++,
    });
  }

  out.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a._tie - b._tie);
  return out.map(({ _tie, ...cat }) => cat);
}

export function formatLohnartDisplay(lohnart: string): string {
  const trimmed = String(lohnart ?? '').trim();
  return trimmed || '—';
}

export function formatPayrollAdjustmentEuro(amount: number): string {
  return (
    (Number(amount) || 0).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  );
}
